import styled from '@emotion/styled';
import type { PropsWithChildren } from 'react';

import type { MarginProperties, PaddingProperties } from '@jane/shared/reefer';
import { Box, Flex, Skeleton, useMobileMediaQuery } from '@jane/shared/reefer';
import { mediaQueries } from '@jane/shared/reefer-emotion';
import { FilterSkeleton, ProductGridSkeleton } from '@jane/shared/components';

interface SpecialDetailSummaryProps
  extends PropsWithChildren,
    MarginProperties,
    PaddingProperties {
  headerHeight?: number;
}

const SpecialDetailSummary = styled(Box)<{ headerHeight: number }>(
  ({ headerHeight }) => ({
    width: '100%',
    height: 'min-content',
    position: 'relative',
    [mediaQueries.desktop('sm', 'min')]: {
      position: 'sticky',
      top: headerHeight + 48,
      maxWidth: '468px',
    },
  })
);

export const SpecialDetailContent = styled(Box)({
  width: '100%',
});

const BaseLayout = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '24px',
  [mediaQueries.desktop('sm', 'min')]: {
    flexDirection: 'row',
    gap: '48px',
  },
});

const SpecialDetailBanner = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 'var(--layers-header)',
  transform: 'translateY(-100%)',
  transition: 'transform 160ms ease',
  '#special-detail-layout.summary-not-at-top &': {
    transform: 'translateY(0)',
  },
  [mediaQueries.desktop('sm', 'min')]: {
    display: 'none',
  },
});

const SpecialDetailSummaryWithAnchor = ({
  children,
  headerHeight = 0,
  ...spacing
}: SpecialDetailSummaryProps) => {
  return (
    <SpecialDetailSummary {...spacing} headerHeight={headerHeight}>
      {children}
      <Box
        id="bottom-of-summary-anchor"
        height="1px"
        width="1px"
        position="absolute"
        bottom={-48}
      />
    </SpecialDetailSummary>
  );
};

const BaseLayoutWithObserver = ({
  children,
  isLoading,
  ...spacing
}: SpecialDetailSummaryProps & { isLoading: boolean }) => {
  const isMobile = useMobileMediaQuery({});
  const parentDocument =
    document.getElementById('shadow-host')?.shadowRoot || document;

  if (
    'IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype
  ) {
    const observer = new IntersectionObserver((entries) => {
      const wrapperElement = parentDocument.querySelector(
        '#special-detail-layout'
      );

      if (!wrapperElement) {
        return;
      }

      if (entries[0].boundingClientRect.y < -1) {
        wrapperElement.classList.add('summary-not-at-top');
      } else {
        wrapperElement.classList.remove('summary-not-at-top');
      }
    });
    const targetElement = parentDocument.querySelector(
      '#bottom-of-summary-anchor'
    );
    if (targetElement) {
      observer.observe(targetElement);
    }
  }

  if (isLoading)
    return (
      <Flex
        gap={48}
        px={isMobile ? 24 : 64}
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <Box width={isMobile ? '100%' : '40%'}>
          <Skeleton.Bone height={isMobile ? 210 : 420} />
        </Box>
        <Box width={isMobile ? '100%' : '60%'}>
          <FilterSkeleton />
          <ProductGridSkeleton length={12} />
        </Box>
      </Flex>
    );

  return (
    <BaseLayout {...spacing} id="special-detail-layout">
      {children}
    </BaseLayout>
  );
};

export const SpecialDetailLayout = Object.assign(BaseLayoutWithObserver, {
  Banner: SpecialDetailBanner,
  Content: SpecialDetailContent,
  Summary: SpecialDetailSummaryWithAnchor,
});
